import { z } from 'zod';
import { SportNameSchema } from './profile.js';
import { TrainerLocationKindSchema, type TrainerLocationKind } from './trainer-profile.js';
import type { BookingDetail } from './booking.js';

/**
 * F33 — book-a-trainer contract. A trainer session is an F16 `bookings` row
 * with `trainer_id` set (no separate table). `POST /me/trainer-bookings`
 * accepts an `Idempotency-Key` HEADER (not in the body) reusing F16's
 * (host_user_id, idempotency_key) index — the booker is host_user_id (Δ5).
 */

export const BookTrainerInputSchema = z
  .object({
    trainerId: z.string().uuid(),
    packageId: z.string().uuid(),
    locationKind: TrainerLocationKindSchema,
    sportName: SportNameSchema,
    // `{ offset: true }` to accept offset timestamps (e.g. +03:00) that mobile/
    // portal emit — matches F16's CreateBookingInputSchema (Wave-2A amendment).
    startAt: z.string().datetime({ offset: true }),
    endAt: z.string().datetime({ offset: true }),
    /** Required when locationKind = 'affiliated_venue'. */
    venueId: z.string().uuid().optional(),
    subVenueId: z.string().uuid().optional(),
    /** Free-form address for client_venue / trainer_own_location modes. */
    sessionLocationText: z.string().max(200).optional(),
    partySize: z.number().int().min(1).max(50).optional(),
    notes: z.string().max(500).optional(),
  })
  .strict()
  .refine(
    (v) =>
      v.locationKind !== 'affiliated_venue' ||
      (v.venueId !== undefined && v.subVenueId !== undefined),
    { message: 'affiliated_venue requires venueId + subVenueId', path: ['locationKind'] },
  )
  .refine(
    (v) =>
      v.locationKind === 'affiliated_venue' ||
      (v.venueId === undefined && v.subVenueId === undefined),
    {
      message: 'client_venue / trainer_own_location must not carry venueId/subVenueId',
      path: ['locationKind'],
    },
  );
export type BookTrainerInput = z.infer<typeof BookTrainerInputSchema>;

/** Extends F16's BookingDetail with the F33 trainer-session fields. */
export type TrainerSessionBookingDetail = BookingDetail & {
  readonly trainerId: string;
  /** Optional so hand-built fixtures stay valid; the API always sets it. */
  readonly trainerDisplayName?: string | null;
  readonly trainerLocationKind: TrainerLocationKind;
  readonly trainerPackageId: string | null;
  readonly trainerSessionLocation: string | null;
};

export const TRAINER_BOOKING_ERROR_CODES = [
  'TRAINER_SLOT_TAKEN',
  'TRAINER_NOT_FOUND',
  // A generic "this request can't be booked" 422 for input that fails the
  // booking's own checks: `endAt` not after `startAt`, a start time that is not
  // in the future, and an unknown `venueId` (all in `BookingService` trainer
  // booking). F09 v2 (§2.2 #1) removed its old verification use: a trainer
  // without a tick (or with a rejected request) stays bookable, and only
  // deactivated / suspended trainers are refused (`TRAINER_DEACTIVATED` /
  // `TRAINER_SUSPENDED`). The name is kept because clients already switch on it.
  'TRAINER_NOT_BOOKABLE',
  'TRAINER_DEACTIVATED',
  'TRAINER_SUSPENDED',
  'LOCATION_MODE_NOT_OFFERED',
  'PACKAGE_NOT_FOUND',
  'PACKAGE_INACTIVE',
  'AFFILIATION_REQUIRED_FOR_VENUE',
  'SUB_VENUE_REQUIRED_FOR_AFFILIATED',
  // FIX 4.2 (D-D2) — the server checks the trainer's published calendar (rules,
  // vacation, blocks, lead and advance windows) and the "accepting new clients"
  // switch, which only lets returning clients through.
  'SLOT_UNAVAILABLE',
  'TRAINER_NOT_ACCEPTING_NEW_CLIENTS',
] as const;
export type TrainerBookingErrorCode = (typeof TRAINER_BOOKING_ERROR_CODES)[number];
